import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useDispatch } from 'react-redux';

import * as uiActions from '~actions/ui-actions';

import * as dialogs from '~components/dialogs/dialog-types';
import ModalDialog from '~components/dialogs/modal-dialog';
import ActionButton from '~components/general/action-button';
import IString from '~components/general/i-string';
import { IdentityProvider } from '~components/identity-providers/identity-provider-prop-types';
import classnames from '~components/util/classnames';

import { translate } from '~i18n/localize';

import { shallowDiffObject } from '~util/compare';
import * as formatter from '~util/formatter';

const IDP_PROPERTIES = {
  NAME: 'name',
  ENTITY_ID: 'entity_id',
  REQUIRED_ATTRIBUTES: 'required_attributes',
  ID_ATTRIBUTE: 'id_attribute',
  EMAIL_ATTRIBUTE: 'email_attribute',
  FIRST_NAME_ATTRIBUTE: 'first_name_attribute',
  LAST_NAME_ATTRIBUTE: 'last_name_attribute',
  METADATA: 'metadata'
};

export default function IdentityProviderDialog({ data = {}, onSubmit }) {
  const { identityProvider = null } = data;
  const isCreateMode = identityProvider === null;

  const dispatch = useDispatch();

  let initialIdentityProvider = null;
  if (isCreateMode) {
    initialIdentityProvider = {
      [IDP_PROPERTIES.NAME]: null,
      [IDP_PROPERTIES.ENTITY_ID]: null,
      [IDP_PROPERTIES.REQUIRED_ATTRIBUTES]: null,
      [IDP_PROPERTIES.ID_ATTRIBUTE]: null,
      [IDP_PROPERTIES.EMAIL_ATTRIBUTE]: null,
      [IDP_PROPERTIES.FIRST_NAME_ATTRIBUTE]: null,
      [IDP_PROPERTIES.LAST_NAME_ATTRIBUTE]: null,
      [IDP_PROPERTIES.METADATA]: null
    };
  } else if (identityProvider) {
    const requiredAttributes = identityProvider[IDP_PROPERTIES.REQUIRED_ATTRIBUTES]
      ? JSON.stringify(identityProvider[IDP_PROPERTIES.REQUIRED_ATTRIBUTES])
      : null;
    initialIdentityProvider = {
      ...identityProvider,
      [IDP_PROPERTIES.REQUIRED_ATTRIBUTES]: requiredAttributes
    };
  }

  const [ identityProviderData, setIdentityProviderData ] = useState(initialIdentityProvider);
  const [ detailErrors, setDetailErrors ] = useState({});
  const [ metadataFileName, setMetadataFileName ] = useState(null);

  const handleChange = (property, value) => {
    setIdentityProviderData(prev => ({ ...prev, [property]: value }));
  };

  const handleClear = (property) => {
    setIdentityProviderData(prev => ({ ...prev, [property]: null }));
    if (property === IDP_PROPERTIES.METADATA) {
      setMetadataFileName(null);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files) {
      const file = e.target.files[0];
      setMetadataFileName(file.name);
      // Read in an XML file, parse it, make the XML pretty, and then set it as metadata
      const reader = new FileReader();
      reader.onload = () => {
        const xmlContent = formatter.formatXml(reader.result);
        setIdentityProviderData(prev => ({ ...prev, [IDP_PROPERTIES.METADATA]: xmlContent }));
      };
      reader.readAsText(file);
    }
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    let errors = {};

    let parsedRequiredAttributes = null;
    try {
      parsedRequiredAttributes = JSON.parse(identityProviderData[IDP_PROPERTIES.REQUIRED_ATTRIBUTES]);
      const isObject = typeof parsedRequiredAttributes === 'object' && !Array.isArray(parsedRequiredAttributes);
      if (!isObject) {
        errors[IDP_PROPERTIES.REQUIRED_ATTRIBUTES] = true;
      }
    } catch (error) {
      errors[IDP_PROPERTIES.REQUIRED_ATTRIBUTES] = true;
    }

    const identityProviderDataCopy = {
      ...identityProviderData,
      [IDP_PROPERTIES.REQUIRED_ATTRIBUTES]: parsedRequiredAttributes
    };

    errors = {
      ...errors,
      [IDP_PROPERTIES.NAME]: !identityProviderDataCopy[IDP_PROPERTIES.NAME],
      [IDP_PROPERTIES.ENTITY_ID]: !identityProviderDataCopy[IDP_PROPERTIES.ENTITY_ID],
      [IDP_PROPERTIES.ID_ATTRIBUTE]: !identityProviderDataCopy[IDP_PROPERTIES.ID_ATTRIBUTE],
      [IDP_PROPERTIES.EMAIL_ATTRIBUTE]: !identityProviderDataCopy[IDP_PROPERTIES.EMAIL_ATTRIBUTE],
      [IDP_PROPERTIES.FIRST_NAME_ATTRIBUTE]: !identityProviderDataCopy[IDP_PROPERTIES.FIRST_NAME_ATTRIBUTE],
      [IDP_PROPERTIES.LAST_NAME_ATTRIBUTE]: !identityProviderDataCopy[IDP_PROPERTIES.LAST_NAME_ATTRIBUTE],
      [IDP_PROPERTIES.METADATA]: !identityProviderDataCopy[IDP_PROPERTIES.METADATA]
    };

    const hasDetailErrors = Object.values(errors).find(element => element !== false);

    if (hasDetailErrors) {
      setDetailErrors(errors);
    } else {
      if (isCreateMode) {
        if (onSubmit) {
          onSubmit(identityProviderDataCopy);
        }
      } else {
        const changed = shallowDiffObject(identityProvider, identityProviderDataCopy);

        dispatch(uiActions.openDialog({
          type: dialogs.CONFIRM_DIALOG,
          data: {
            purpose: (
              <IString
                stringKey="identityProvider.dialog.edit.confirm.title"
                placeholders={{ name: identityProvider.name }}
              />
            ),
            details: (
              <IString
                stringKey="identityProvider.dialog.edit.confirm.details"
                placeholders={{ name: identityProvider.name }}
              />
            ),
            confirm: () => {
              if (onSubmit) {
                onSubmit(changed);
              }
              dispatch(uiActions.closeDialog()); // close confirm dialog
            }
          }
        }));
      }
    }
  };

  return (
    <ModalDialog type={dialogs.IDENTITY_PROVIDER_DIALOG}>
      <div className="purpose">
        { isCreateMode
          ? <IString stringKey="identityProvider.dialog.create.title" />
          : <IString
              stringKey="identityProvider.dialog.edit.title"
              trustPlaceholders
              placeholders={{ name: identityProvider.name, id: identityProvider.id }}
            />
        }
      </div>
      <form noValidate onSubmit={handleSubmit}>
        { Object.entries(IDP_PROPERTIES).map(([ key, property ]) => {
          if (property !== IDP_PROPERTIES.METADATA) { // Skip metadata field here, handled separately below
            return (
              <fieldset key={key} className="chunk">
                <label htmlFor={property} className={classnames('fieldLabel', { error: detailErrors[property] })}>
                  <IString stringKey={`identityProvider.properties.${property}`} />
                  { detailErrors[property]
                    ? <IString stringKey={`identityProvider.dialog.errors.${property}`} className="errorText" />
                    : null
                  }
                </label>
                <div className="flex">
                  <input
                    className="fullWidth"
                    id={property}
                    onChange={e => handleChange(property, e.target.value)}
                    required
                    type="text"
                    value={identityProviderData[property] || ''}
                    placeholder={property === IDP_PROPERTIES.REQUIRED_ATTRIBUTES ? translate('identityProvider.dialog.required_attributes_placeholder') : ''}
                  />
                  <div className="actionBar inline" style={{ paddingTop: '3px' }}>
                    <ActionButton className="delete noTitle" onClick={() => handleClear(property)} />
                  </div>
                </div>
              </fieldset>
            );
          }
        })}

        { isCreateMode &&
          <fieldset className="chunk">
            <label htmlFor={IDP_PROPERTIES.METADATA} className={classnames('fieldLabel', { error: detailErrors[IDP_PROPERTIES.METADATA] })}>
              <IString stringKey={`identityProvider.properties.${IDP_PROPERTIES.METADATA}`} />
              { detailErrors[IDP_PROPERTIES.METADATA]
                ? <IString stringKey={`identityProvider.dialog.errors.${IDP_PROPERTIES.METADATA}`} className="errorText" />
                : null
              }
            </label>
            <div className="flex">
              <div className="actionBar showTitles">
                <input type="file" id="metadataUpload" style={{ display: 'none' }} accept="application/xml" onChange={handleFileChange} />
                <ActionButton
                  className="share"
                  title={translate('general.upload')}
                  onClick={() => document.getElementById('metadataUpload').click()}
                />
              </div>
              <input
                className="fullWidth"
                disabled
                type="text"
                placeholder={translate('identityProvider.dialog.create.placeholders.metadata')}
                value={metadataFileName || ''}
              />
              <div className="actionBar inline" style={{ paddingTop: '3px' }}>
                <ActionButton className="delete noTitle" onClick={() => handleClear(IDP_PROPERTIES.METADATA)} />
              </div>
            </div>
          </fieldset>
        }

        <div className="actions">
          <button type="submit"><IString stringKey="general.save" /></button>
        </div>

      </form>
    </ModalDialog>
  );

}

IdentityProviderDialog.propTypes = {
  data: PropTypes.shape({
    identityProvider: IdentityProvider
  }),
  onSubmit: PropTypes.func
};
